import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Loader2 } from "lucide-react";
import { ErrorBoundary } from "@/components/shared/error-boundary";
import { Field, FieldGrid, FormSection, ToggleField } from "@/components/shared/form";
import { Button } from "@/components/ui/button";
import { Input, NativeSelect } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  materialToForm,
  useCreateMaterial,
  useMaterial,
  useMaterials,
  useUoms,
  useUpdateMaterial,
} from "@/features/materials/api";
import { MATERIAL_CATEGORIES } from "@/features/materials/constants";
import {
  emptyMaterialForm,
  materialFormSchema,
  type MaterialFormValues,
} from "@/features/materials/schema";

export function MaterialNewPage() {
  return (
    <ErrorBoundary title="Material form crashed">
      <MaterialFormPage mode="create" />
    </ErrorBoundary>
  );
}

export function MaterialEditPage({ materialId }: { materialId: string }) {
  return (
    <ErrorBoundary title="Material form crashed">
      <MaterialFormPage mode="edit" materialId={materialId} />
    </ErrorBoundary>
  );
}

function MaterialFormPage({
  mode,
  materialId,
}: {
  mode: "create" | "edit";
  materialId?: string;
}) {
  const navigate = useNavigate();
  const isEdit = mode === "edit";
  const { data: material, isLoading } = useMaterial(isEdit ? materialId : undefined);
  const { data: uoms = [] } = useUoms();
  const { data: materials = [] } = useMaterials();
  const createMaterial = useCreateMaterial();
  const updateMaterial = useUpdateMaterial();

  const form = useForm<MaterialFormValues>({
    resolver: zodResolver(materialFormSchema),
    defaultValues: emptyMaterialForm,
    mode: "onBlur",
  });

  const { errors, isDirty } = form.formState;

  useEffect(() => {
    if (isEdit && material) form.reset(materialToForm(material));
  }, [isEdit, material, form]);

  useEffect(() => {
    if (!isEdit && uoms.length && !form.getValues("uom_id")) {
      const ton = uoms.find((u) => u.code === "TON");
      if (ton) form.setValue("uom_id", ton.id);
    }
  }, [isEdit, uoms, form]);

  const submit = form.handleSubmit(async (values) => {
    if (isEdit && materialId) {
      await updateMaterial.mutateAsync({ id: materialId, values });
      void navigate({ to: "/materials" });
      return;
    }
    await createMaterial.mutateAsync(values);
    void navigate({ to: "/materials" });
  });

  const saving = createMaterial.isPending || updateMaterial.isPending;
  const parentOptions = materials.filter((m) => m.id !== materialId);

  if (isEdit && isLoading) {
    return <p className="py-16 text-center text-sm text-muted-foreground">Loading material…</p>;
  }

  return (
    <div className="mx-auto max-w-3xl space-y-3 pb-20">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button asChild variant="ghost" size="icon-sm" aria-label="Back">
            <Link to="/materials">
              <ArrowLeft />
            </Link>
          </Button>
          <div>
            <h1 className="text-lg font-semibold tracking-[-0.01em]">
              {isEdit ? "Edit material" : "New material"}
            </h1>
            <p className="text-xs text-muted-foreground">
              Catalogue master used by inventory, purchase and sales
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <Button asChild type="button" variant="outline" size="sm">
            <Link to="/materials">Cancel</Link>
          </Button>
          <Button type="button" size="sm" disabled={saving || (isEdit && !isDirty)} onClick={() => void submit()}>
            {saving ? <Loader2 className="animate-spin" /> : null}
            Save
          </Button>
        </div>
      </header>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
        className="space-y-3"
      >
        <FormSection id="identity" title="Identity" description="Name, category and parent chain">
          <FieldGrid>
            <Field label="Name" required span={6} error={errors.name?.message} htmlFor="name">
              <Input id="name" autoFocus {...form.register("name")} />
            </Field>
            <Field label="Category" span={6} htmlFor="category">
              <NativeSelect id="category" {...form.register("category")}>
                {MATERIAL_CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </NativeSelect>
            </Field>
            <Field
              label="Parent material"
              span={6}
              hint="Optional — e.g. Slit from Coil"
              htmlFor="parent_material_id"
            >
              <NativeSelect id="parent_material_id" {...form.register("parent_material_id")}>
                <option value="">None</option>
                {parentOptions.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </NativeSelect>
            </Field>
            <ToggleField
              label="Active"
              description="Inactive materials stay in history but are hidden from new docs"
              checked={form.watch("is_active")}
              onChange={(v) => form.setValue("is_active", v, { shouldDirty: true })}
              span={6}
            />
          </FieldGrid>
        </FormSection>

        <FormSection id="measure" title="Measure" description="UOM, batch prefix, shape requirement">
          <FieldGrid>
            <Field label="UOM" required span={4} error={errors.uom_id?.message} htmlFor="uom_id">
              <NativeSelect id="uom_id" {...form.register("uom_id")}>
                <option value="">Select…</option>
                {uoms
                  .filter((u) => u.is_active || u.id === form.watch("uom_id"))
                  .map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.code} — {u.name}
                    </option>
                  ))}
              </NativeSelect>
            </Field>
            <Field
              label="Batch prefix"
              required
              span={4}
              error={errors.batch_prefix?.message}
              hint="Used when generating inventory batch numbers"
              htmlFor="batch_prefix"
            >
              <Input id="batch_prefix" className="uppercase" {...form.register("batch_prefix")} />
            </Field>
            <ToggleField
              label="Requires shape / size"
              description="Show shape & size pickers on inventory and sales lines"
              checked={form.watch("requires_shape_size")}
              onChange={(v) => form.setValue("requires_shape_size", v, { shouldDirty: true })}
              span={4}
            />
          </FieldGrid>
        </FormSection>

        <FormSection id="notes" title="Notes">
          <Field label="Description" span={12} htmlFor="description">
            <Textarea id="description" rows={3} {...form.register("description")} />
          </Field>
        </FormSection>
      </form>
    </div>
  );
}
